import { getTextAttrByDocElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import type { DocumentElement } from '@tencent/alloy-editor';
import type {
    IParagraph,
    ITextBody,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import {
    ITextBodyStyle,
    ITextRunStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import merge from 'lodash/merge';
import {
    IChartTextProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property.interface';
import {
    CHART_DEFAULT_PADDING_LR,
    CHART_DEFAULT_PADDING_T,
    CHART_DEFAULT_PADDING_B,
} from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import {
    getEffectFilter,
    getFillStyle,
    getStrokeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';

export const convertToDocumentElement = (content: string, style): DocumentElement => ({
    type: 'document',
    children: [
        {
            type: 'paragraph',
            children: [
                {
                    type: 'text',
                    text: content,
                    fontSize: style.fontSize,
                    fontWeight: style.fontWeight,
                    fontStyle: style.fontStyle,
                },
            ],
        },
    ],
});


/**
 * 根据元素的属性，生成最终的 g2 文本样式
 */
export function getTextStyle(props: any, { defaultProps }: { defaultProps?: any } = {}) {
    const paragraphList = getParaList(props, defaultProps);
    const textDocElement = getTextDocElement(paragraphList);

    const { style } = getTextAttrByDocElement(textDocElement);

    return style!;
}

export function getTextDocElement(paragraphList, textBodyStyle?): DocumentElement {
    const docElement = EditorDataModel.convertFromTextModel(
        {
            paragraphList,
            textBodyStyle: textBodyStyle ? textBodyStyle : {
                bodyStyle: { paddingBottom: 0, paddingLeft: 0, paddingTop: 0, paddingRight: 0 },
            },
        },
        { noConvertBack: true },
    );
    return docElement;
}

// eslint-disable-next-line @typescript-eslint/member-ordering
export function getParaList(props: any, defaultProps?: any): IParagraph[] {
    const paraList = props.paragraphList;
    const firstRun =  paraList?.[0]?.textRuns?.[0];
    const defaultRunProps = paraList?.[0]?.paragraphProps?.defaultRunProps;
    return [{
        ...paraList?.[0],
        textRuns: [{
            text: firstRun?.text || '',
            styles: {
                // eslint-disable-next-line @tencent/i18next/no-literal-string
                fontFamily: defaultRunProps?.fonts?.latin || defaultRunProps?.fonts?.ea || '默认字体',  // 无字体，使用默认字体
                ...defaultRunProps,
                ...defaultProps?.paragraphList?.[0].paragraphProps?.defaultRunProps,
                ...firstRun?.styles,
            },
            type: 'run',
        }],
    }];
}

/**
 * 根据chart文本元素信息，生成document element
 * 文本可能是：数据标签、坐标轴标题、legend等等
 * @param chartTextProps
 */
export function createDocumentWithChartTextProps(chartTextProps: IChartTextProperty<any>) {
    const paragraphList = chartTextProps.getParagraphList();
    const textBodyStyle = chartTextProps.getTextBodyStyle();
    return getTextDocElement(paragraphList, textBodyStyle);
}

/**
 * 根据继承规则，创建对应的doc element
 * @param source 自定义textBody信息
 * @param defaultProps 继承的默认textBody信息
 * @param content 文本内容
 * @param option 配置项
 */
export function createDocElement(
    source?: ITextBody, defaultProps?: ITextBody, content?: string,
    option?: { noDefaultPadding?: boolean, defaultAlignCenter?: boolean,
        anchorLeft?: boolean, defaultLineHeight?: number },
) {
    const defaultRunProps = defaultProps?.paragraphList?.[0]?.paragraphProps?.defaultRunProps;
    const paragraphList = createParaList(
        source?.paragraphList, defaultRunProps, content,
        { defaultAlignCenter: option?.defaultAlignCenter ?? true, defaultLineHeight: option?.defaultLineHeight },
    );
    const textBodyStyle = mergeTextBodyStyle(
        source?.textBodyStyle,
        defaultProps?.textBodyStyle,
        { noDefaultPadding: option?.noDefaultPadding ?? true, anchorLeft: option?.anchorLeft },
    );
    return getTextDocElement(paragraphList, textBodyStyle);
}

export function createParaList(
    sourceParaList?: IParagraph[], defaultRunProps?: ITextRunStyle,
    content?, option?: { defaultAlignCenter: boolean, defaultLineHeight?: number },
) {
    const align = option?.defaultAlignCenter ? 'center' : 'left';  // 无值情况，默认居中
    const lineHeight = option?.defaultLineHeight;
    if (sourceParaList) {
        return sourceParaList.map((para) => {
            const curDefaultRunProps = para?.paragraphProps?.defaultRunProps;
            const textRuns = para.textRuns?.map((textRun) => {
                const runStyles = {
                    ...defaultRunProps,
                    ...curDefaultRunProps,
                    ...textRun?.styles,
                };
                return { ...textRun, styles: runStyles };
            }) || [];
            // 无值情况，默认居中
            return { ...para, textRuns, paragraphProps: { lineHeight, textAlign: align, ...para?.paragraphProps } };
        });
    }
    return [{
        paragraphProps: { textAlign: align, lineHeight },
        textRuns: [{
            text: content || '',
            styles: {
                ...defaultRunProps,
            },
        }],
    }];
}

// eslint-disable-next-line complexity
export function mergeTextBodyStyle(
    source?: ITextBodyStyle,
    defaultStyle?: ITextBodyStyle,
    option?: { noDefaultPadding: boolean, anchorLeft?: boolean },
) {
    let textBodyStyle = source || defaultStyle;
    if (source && defaultStyle) {
        textBodyStyle = merge({}, defaultStyle, source);
    }
    const defaultPaddingT = option?.noDefaultPadding ? 0 : CHART_DEFAULT_PADDING_T;
    const defaultPaddingB = option?.noDefaultPadding ? 0 : CHART_DEFAULT_PADDING_B;
    const defaultPaddingLR = option?.noDefaultPadding ? 0 : CHART_DEFAULT_PADDING_LR;

    const { bodyStyle } = textBodyStyle || { };
    const paddingLeft = bodyStyle?.paddingLeft ?? defaultPaddingLR;
    const paddingTop = bodyStyle?.paddingTop ?? defaultPaddingT;
    const paddingRight = bodyStyle?.paddingRight ?? defaultPaddingLR;
    const paddingBottom = bodyStyle?.paddingBottom ?? defaultPaddingB;
    const newBodyStyle = { ...bodyStyle, paddingLeft, paddingRight, paddingTop, paddingBottom  };
    textBodyStyle = { ...textBodyStyle, bodyStyle: newBodyStyle };

    if (textBodyStyle && option?.anchorLeft) {
        const { wrapperStyle } = textBodyStyle;
        const newWrapperStyle = { ...wrapperStyle, alignItems: undefined, justifyContent: undefined  };
        textBodyStyle = { ...textBodyStyle, wrapperStyle: newWrapperStyle };
    }
    return textBodyStyle;
}

export function updateDocElementText(docElement: DocumentElement, content: string) {
    const run = docElement?.children[0]?.children[0];
    if (run) {
        run.text = `${content ?? ''}`;
    }
}

